// Single-source CSS keyframes injected globally from main.tsx. All motion
// in the shell composes one of these keyframes with the timing and easing
// helpers below. The reduced-motion media query collapses every transform
// animation to a pure opacity fade so we still respect the OS-level setting
// without losing structural feedback.

import { DIST, DUR, EASE } from './tokens.js';

export const MOTION_GLOBAL_CSS = `
  @keyframes chirp-page-in-forward {
    from { opacity: 0; transform: translateY(${DIST.page}px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes chirp-page-in-backward {
    from { opacity: 0; transform: translateY(-${DIST.page}px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes chirp-question-in-forward {
    from { opacity: 0; transform: translateX(${DIST.drawer}px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes chirp-question-in-backward {
    from { opacity: 0; transform: translateX(-${DIST.drawer}px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes chirp-field-in {
    from { opacity: 0; transform: translateY(${DIST.field}px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes chirp-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes chirp-progress-pulse {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(400%); }
  }
  /* One-shot bounce: card fades and translates up from below, then a small
     bounce settle. Used by NudgeTeaser to draw the eye on first show without
     being annoying enough to repeat. */
  @keyframes chirp-nudge-in {
    0% { opacity: 0; transform: translateY(24px); }
    60% { opacity: 1; transform: translateY(-4px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  /* Surface-level entry curves. Centralised here so SurveySkeleton,
     FeedbackSkeleton, AnnouncementSkeleton, and the announcement shells all
     share one curve — drift creates visible inconsistency when the same modal
     opens via different code paths. */
  @keyframes chirp-modal-in {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes chirp-drawer-right-in {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
  @keyframes chirp-drawer-left-in {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }
  @keyframes chirp-banner-top-in {
    from { opacity: 0; transform: translateY(-100%); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes chirp-banner-bottom-in {
    from { opacity: 0; transform: translateY(100%); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes chirp-backdrop-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  /* Universal button polish — every chirp-btn in the shell gets the same
     lift-on-hover and press-on-active feel without each component having
     to reimplement onMouseDown state. touch-action: manipulation removes
     the 300 ms tap delay on iOS and disables double-tap-to-zoom on the
     button itself. -webkit-tap-highlight-color: transparent hides the
     default grey flash on Android Chrome. */
  .chirp-btn {
    transition:
      opacity 120ms ease,
      background 160ms ease,
      transform 80ms ${EASE.out},
      box-shadow 180ms ease;
    will-change: transform;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
  }
  .chirp-btn:hover:not(:disabled) {
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
  }
  .chirp-btn:active:not(:disabled) {
    transform: scale(0.97);
  }
  .chirp-btn:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
  /* Mobile survey shell: at phone widths the desktop backdrop padding
     eats half the usable width and squeezes scale buttons below the 44 px
     tap-target threshold. These overrides let the modal hug the viewport
     edges and bump tap heights so NPS / scale rows stay thumb-friendly.
     Targets class hooks on SurveyShell, PagedSurvey, and WizardFooter. */
  @media (max-width: 480px) {
    .chirp-survey-backdrop { padding: 0 !important; align-items: stretch !important; }
    .chirp-survey-container {
      max-height: 100dvh !important;
      max-width: 100% !important;
      border-radius: 0 !important;
      border-left: none !important;
      border-right: none !important;
    }
    .chirp-survey-close { top: 8px !important; right: 8px !important; }
    .chirp-survey-page-body { padding: 20px 16px !important; }
    .chirp-survey-footer { padding: 12px 16px !important; }
    .chirp-survey-scale-button { min-height: 44px !important; }
    /* FeedbackWidget NPS row: bigger tap targets and a tighter gap so 11
       buttons stay on a single line at 320–390 px without looking cramped. */
    .chirp-fb-numeric-row { gap: 4px !important; }
    .chirp-fb-numeric-button { min-height: 44px !important; font-size: 13px !important; }
  }
  @media (prefers-reduced-motion: reduce) {
    @keyframes chirp-page-in-forward { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-page-in-backward { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-question-in-forward { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-question-in-backward { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-field-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-modal-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-drawer-right-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-drawer-left-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-banner-top-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-banner-bottom-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes chirp-nudge-in { from { opacity: 0; } to { opacity: 1; } }
    .chirp-btn:active:not(:disabled) { transform: none; }
  }
`;

export type Direction = 'forward' | 'backward';

// Page transitions use the same decelerate easing as conversational question
// transitions. Overshoot looks great on a single small element (modal entry,
// nudge) but reads as a "slap" when the whole content area bounces — calm
// out-easing is what keeps a 4-question page feeling smooth.
export const pageAnim = (direction: Direction): string =>
  `chirp-page-in-${direction} ${DUR.page}ms ${EASE.out} both`;

export const questionAnim = (direction: Direction): string =>
  `chirp-question-in-${direction} ${DUR.base}ms ${EASE.out} both`;

export const fieldAnim = (delayMs = 0): string =>
  `chirp-field-in ${DUR.base}ms ${EASE.out} ${delayMs}ms both`;

export const fadeIn = (durationMs = DUR.fast): string =>
  `chirp-fade-in ${durationMs}ms ${EASE.out} both`;

// Surface entry helpers — all shells (and matching skeletons) share these so
// the same modal opens with the same curve regardless of code path.
export const modalAnim = (): string =>
  `chirp-modal-in ${DUR.modal}ms ${EASE.overshoot} both`;

export const drawerAnim = (side: 'left' | 'right'): string =>
  `chirp-drawer-${side}-in 320ms ${EASE.out} both`;

export const bannerAnim = (side: 'top' | 'bottom'): string =>
  `chirp-banner-${side}-in 280ms ${EASE.out} both`;

export const backdropAnim = (): string =>
  `chirp-backdrop-in 200ms ${EASE.out} both`;
